'use client';
import { useState, useEffect } from 'react';
import Image from 'next/image'; // Import the Image component
import ReviewStars from '@/components/product/reviewStars/reviewStars';
import { useProduct } from '@/components/product/product-context';
import offsetBlockClass from '@/utils/offsetBlockClass';
/*
  Imports Personalize context and utility functions
  import { usePersonalize } from '@/lib/contentstack/PersonalizeContext';
  */

export default function ProductTitle({ block, position }) {
  const {
    hide_review_stars,
    show_offset_block_selector,
    _metadata,
    shipping_copy
  } = block.product_title;
  const { settings, productReviewData, title, superscript_title } =
    useProduct();
  const [reviewData, setReviewData] = useState(productReviewData);
  const [isClient, setIsClient] = useState(false);

  /*
    Initialize Contentstack Personalize 
    const personalize = usePersonalize();
   */

  /* 
    This effect runs when the personalize context is available
    and when the component mounts. It checks for active variants in the manifest cookie
    and triggers impressions for those variants.
    useEffect(() => {
      const trigger = async () => {
        if (!personalize) return;

        const manifest = getManifestFromCookie();
        if (!manifest || !manifest.experiences) return;

        const experienceUids = manifest.experiences
        .filter((exp) => exp.activeVariantShortUid !== null)
        .map((exp) => exp.shortUid);

        if (experienceUids.length === 0) return;

        try {
          await personalize.triggerImpressions({ experienceShortUids: experienceUids });
        } catch (error) {
          console.error('Failed to trigger impressions:', error);
        }
      };
      trigger();
    }, [personalize]);
  */

  useEffect(() => {
    setIsClient(true);

    // Check if the Contentstack setting for referencing a different product is enabled
    if (settings?.referenced_product_id) {
      fetch(`/api/reviews?productId=${settings.referenced_product_id}`)
        .then((response) => response.json())
        .then((data) => {
          setReviewData(data);
        })
        .catch((error) => {
          console.error('Error fetching referenced product reviews:', error);
        });
    }
  }, [settings?.referenced_product_id]);

  const { data, totalLeesaReviewCount, productRatingValue } = reviewData ?? {};

  let titleStr1 = superscript_title;
  let titleStr2 = '';
  let superscript = '';

  //if there is a superscript symbol, split it out from the rest of the text so that we can style it to be smaller
  if (superscript_title?.length > 0) {
    if (superscript_title?.includes('®')) {
      titleStr1 = superscript_title.split('®')[0];
      titleStr2 = superscript_title.split('®')[1];
      superscript = '®';
    } else if (superscript_title?.includes('™')) {
      titleStr1 = superscript_title.split('™')[0];
      titleStr2 = superscript_title.split('™')[1];
      superscript = '™';
    } else if (superscript_title?.includes('©')) {
      titleStr1 = superscript_title.split('©')[0];
      titleStr2 = superscript_title.split('©')[1];
      superscript = '©';
    }
  }
  const { bottomline } = data ?? {};

  function handleClick(e) {
    e.preventDefault();
    const reviewEl = document.querySelector(
      "[data-name='ratings-reviews-section']"
    );
    if (reviewEl) {
      const scrollOffset = reviewEl.children?.length ? 50 : -800;
      const topposition = reviewEl.offsetTop - scrollOffset;

      window.scrollTo({
        top: topposition,
        left: 0,
        behavior: 'smooth'
      });
    }
  }

  const roundedStarCount = (
    Math.round(parseFloat(productRatingValue, 10) * 2) / 2
  ).toFixed(1);
  const requiresHalfStar = roundedStarCount.includes('.5');

  const showShippingCopy = shipping_copy;

  return (
    <section
      id={`${_metadata.uid}_${position}`}
      data-name="pdp-product-title"
      className={offsetBlockClass(
        show_offset_block_selector,
        'mb-8 block w-full'
      )}
    >
      <div className="mb-[4px] sm:flex sm:justify-between lg:mb-[8px]">
        {superscript_title?.length > 0 ? (
          <h1
            id="product-title"
            className="h3 relative flex flex-wrap text-white sm:text-left"
          >
            <span className="block">{titleStr1}</span>
            {superscript?.length > 0 && (
              <span className="body-xs inline-block self-start pr-1">
                {superscript}
              </span>
            )}
            <span className="block">{titleStr2}</span>
          </h1>
        ) : (
          <h1
            id="product-title"
            className="h3 col-span-4 col-start-1 text-white sm:col-span-3 sm:text-left"
          >
            {title}
          </h1>
        )}
      </div>
      {hide_review_stars ||
        (totalLeesaReviewCount && bottomline && (
          <div className="mb-4 flex min-w-25 items-center">
            {bottomline.total_review > 0 && (
              <>
                <ReviewStars
                  roundedStarCount={roundedStarCount}
                  requiresHalfStar={requiresHalfStar}
                  onClickFunction={handleClick}
                />
                <p
                  className={`body-xs pl-1.5 text-["text-white"] underline hover:cursor-pointer`}
                  onClick={(e) => handleClick(e)}
                >
                  {bottomline.total_review > 1000
                    ? bottomline.total_review?.toLocaleString('en-US', {
                        minimumIntegerDigits: 2
                      })
                    : bottomline.total_review}{' '}
                  Reviews
                </p>
              </>
            )}

            <p
              className={`body-xs ${bottomline.total_review > 0 ? 'ml-3' : ''} text-[#EDCC6C]`}
            >
              {-Math.round(-totalLeesaReviewCount / 1000)}
              k+ Leesa Reviews
            </p>
            {isClient && (
              <Image
                src="/images/WavyCheck.svg"
                alt="Verified Check Icon"
                width="16"
                height="16"
                sizes="16px"
                className="ml-[5px]"
              />
            )}
          </div>
        ))}
      {showShippingCopy && (
        <div className="min-h-[20px]">
          {isClient && (
            <div id="delivery-window" className={`body-s text-light-green`}>
              <p className="pl-1 leading-tight">
                &#x2022;
                <span className="pl-2">{shipping_copy}</span>
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
